import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { usePreferences } from './usePreferences';

function scoreProperty(property, prefs) {
  let score = 0;
  const reasons = [];

  if (prefs.budget_max) {
    if (property.asking_price <= prefs.budget_max) {
      score += 3;
      reasons.push('within budget');
    } else if (property.asking_price <= prefs.budget_max * 1.1) {
      score += 1;
      reasons.push('slightly above budget');
    }
  }
  if (prefs.budget_min && property.asking_price >= prefs.budget_min) {
    score += 1;
  }
  if (prefs.min_bedrooms && property.beds >= prefs.min_bedrooms) {
    score += 2;
    reasons.push(`${property.beds}+ bedrooms`);
  }
  if (prefs.neighborhood) {
    const needle = prefs.neighborhood.toLowerCase();
    const haystack = `${property.address || ''} ${property.city || ''}`.toLowerCase();
    if (haystack.includes(needle)) {
      score += 3;
      reasons.push(`in ${prefs.neighborhood}`);
    }
  }

  const propFeatures = (property.features || []).map(f => String(f).toLowerCase());
  const wantedFeatures = prefs.special_features || [];
  const matchedFeatures = wantedFeatures.filter(f => propFeatures.includes(String(f).toLowerCase()));
  if (matchedFeatures.length) {
    score += matchedFeatures.length * 2;
    reasons.push(...matchedFeatures);
  }

  return { score, reasons };
}

// Builds a personalized shortlist of active listings by pre-filtering
// properties against the buyer's saved preferences, then ranking the
// remaining matches by how well they fit (budget, bedrooms, neighborhood,
// and requested features).
export function useShortlist() {
  const { preferences, loading: prefsLoading } = usePreferences();
  const [shortlist, setShortlist] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchShortlist = useCallback(async () => {
    if (!preferences) {
      setShortlist([]);
      setLoading(false);
      return;
    }
    setLoading(true);

    let query = supabase
      .from('properties')
      .select('*')
      .in('status', ['listed', 'new'])
      .order('listed_at', { ascending: false })
      .limit(50);

    if (preferences.city) query = query.ilike('city', `%${preferences.city}%`);
    if (preferences.state) query = query.ilike('state', preferences.state);
    if (preferences.min_bedrooms) query = query.gte('beds', preferences.min_bedrooms);
    if (preferences.budget_max) query = query.lte('asking_price', preferences.budget_max * 1.1);
    if (preferences.budget_min) query = query.gte('asking_price', preferences.budget_min * 0.8);

    const { data } = await query;

    const scored = (data || [])
      .map(property => ({ property, ...scoreProperty(property, preferences) }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 12);

    setShortlist(scored);
    setLoading(false);
  }, [preferences]);

  useEffect(() => {
    fetchShortlist();
  }, [fetchShortlist]);

  return {
    shortlist,
    loading: loading || prefsLoading,
    hasPreferences: !!preferences,
    refetch: fetchShortlist,
  };
}
